// Ortak (web-expert + web-supplier; S37-0) — Tehlike sınıfı (hazard) sunumu (S34-2 design review §1; owner review
// B1 item 1, 2026-09-28 — tek sunum kararı). Eski `permitEnums.ts:172` `hazardTagColor`
// (durum `Tag`si gibi DOLU kırmızı/gri pil) yerine geçer: hazard bir DURUM (Carbon "status")
// değil bir SINIFLANDIRMA, bu yüzden hiçbir zaman bir durum rozeti gibi GÖRÜNMEMELİ (review §1
// "Diagnosis"). `hazardPresentation(1|2|3)` TEK eşleme — i18n anahtarı + işaretçi rengi —
// TEK sunumda (outline `Tag`) OKUNUR. Eski ikinci "satır-içi gösterge" varyantı (`/permits`
// başlık alt satırı, kenarlıksız metin+işaretçi) B1 item 1 ile KALDIRILDI — Çok Tehlikeli
// (kırmızı) ve Tehlikeli (eski `risk-high` alias'ı, `#c2410c`) o küçük metinde neredeyse AYNI
// görünüyordu; tek düzeltme kararı "her yerde TEK hazard biçemi: outline Tag" oldu, ikinci bir
// biçem daha AZ karışıklık riski taşımaz.
//
// Renkler `theme-carbon.css`'in `--color-hazard-*` alias'ları (M2) — `--color-risk-*`'ye İKİNCİ
// bir anlam YÜKLEMEZ. **Tehlikeli artık `risk-high`'a alias DEĞİL** (B1 item 1): risk-high owner
// review B1 item 2 ile Carbon Orange 40'a taşındı (metin ÇİFTİ için ayarlı, dolu rozet+metin),
// ama bu küçük 8×8 işaretçi TEK BAŞINA yüzeye karşı ≥3:1 (WCAG 1.4.11) taşımak ZORUNDA — Orange
// 40 kum/beyaz yüzeyde yalnız 2.5:1 ölçer. `--color-hazard-high` bu yüzden KENDİ Carbon Orange
// 50 literal'ini taşır (yüzey üstünde ≥3:1 + kırmızıdan hue olarak AÇIKÇA ayrı — bkz. o
// token'ın kendi yorumu). Az Tehlikeli (sarı) TEK BAŞINA 1.63:1 ölçtüğünden (review §1
// "Contrast") 1px `hazard-low-edge` halkası ZORUNLU; diğer ikisi halkasız yeterli kontrasta
// sahip. Metin rengi HER ZAMAN `text-fg` (Tag'in outline varyantı) — hazard rengi asla METİN
// rengi olarak kullanılmaz (sarı METİN 1.63:1, review'ün açıkça reddettiği).

// NOT: `bg-hazard-*` / `ring-hazard-low-edge` token'ları YALNIZ Carbon build'lerinde
// (`theme-carbon.css`) tanımlı; Carbon'u import etmeyen bir app bu bileşeni render ETMEMELİ.
// i18n: `hazard.1/2/3` anahtarları app kataloğundan gelir (Pagination → `pagination.summary` emsali).

import { useTranslation } from 'react-i18next';
import type { HazardClass } from '../api/contractors';
import { Tag } from './Tag';
import { cn } from './cn';

export interface HazardPresentation {
  /** i18n anahtarı — neutral `hazard.*` namespace (S34-2: `contractors.hazard.*` +
   * `permits.hazard.*` bu tek isimde BİRLEŞTİ, ölü anahtarlar silindi). */
  labelKey: string;
  /** İşaretçinin zemin rengi + (yalnız Az Tehlikeli) kontrast halkası — boyut/şekil TAŞIMAZ,
   * onu render eden `HazardMarker` verir (tek kaynak, iki tüketici arasında KAYMAZ). */
  markerClassName: string;
}

export function hazardPresentation(hazardClass: HazardClass): HazardPresentation {
  switch (hazardClass) {
    case 3:
      return { labelKey: 'hazard.3', markerClassName: 'bg-hazard-very-high' };
    case 2:
      return { labelKey: 'hazard.2', markerClassName: 'bg-hazard-high' };
    case 1:
      return {
        labelKey: 'hazard.1',
        markerClassName: 'bg-hazard-low ring-1 ring-inset ring-hazard-low-edge',
      };
  }
}

/** 8×8 kare işaretçi (review §1 mockup "■") — `rounded-none`, `Tag`in `icon` slotunun 16×16
 * kutusunda ortalanır. */
function HazardMarker({ hazardClass }: { hazardClass: HazardClass }) {
  return (
    <span
      className={cn('h-2 w-2 rounded-none', hazardPresentation(hazardClass).markerClassName)}
      aria-hidden="true"
    />
  );
}

/** (a) Outline Tag — özel hazard sütunları (izin tipleri listesi, taşeron listesi) ve detay
 * başlıkları (izin, taşeron, departman kontrolü). Detay başlığında SIRA: durum önce, hazard
 * sonra (review §1). */
export function HazardTag({
  hazardClass,
  className,
}: {
  hazardClass: HazardClass;
  className?: string;
}) {
  const { t } = useTranslation();
  const { labelKey } = hazardPresentation(hazardClass);
  return (
    <Tag variant="outline" icon={<HazardMarker hazardClass={hazardClass} />} className={className}>
      {t(labelKey)}
    </Tag>
  );
}
